import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AssetClassSchema, type AssetClassDto } from '@taqon/contracts';
import { patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { CLASSES, classTree, useAssetClasses } from './shared';

/**
 * Asset classes and sub-classes (FRD-AST-008, AST-009): configuration the business maintains. A
 * sub-class sits under a top-level class; there is no third level. Classes carry no
 * capitalization threshold or depreciation default (Ph2, CLR-05 / CLR-07) and are never deleted.
 */
export function AssetClassesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'assets.asset_class:create');
  const canChange = hasCapability(session.data, 'assets.asset_class:change');
  const classes = useAssetClasses();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AssetClassDto | null>(null);
  const rows = classes.data?.items ?? [];

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Asset classes"
        description="The classes and sub-classes every asset is classified by."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setCreating(true);
              }}
            >
              New class
            </Button>
          ) : undefined
        }
      />
      {classes.isError && <ErrorState message={classes.error.message} />}
      <Section>
        {classes.isPending && <LoadingState label="Loading asset classes" />}
        {classes.data !== undefined && rows.length === 0 && (
          <EmptyState
            title="No asset classes yet"
            detail="Classes are configured by the business; none is provided by the system."
          />
        )}
        {rows.length > 0 && (
          <Table size="small" aria-label="Asset classes">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Status</TableCell>
                {canChange && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {classTree(rows).map(({ row, depth }) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box
                      component="span"
                      sx={{ pl: depth * 3, fontWeight: depth === 0 ? 600 : 400 }}
                    >
                      {row.code}
                    </Box>
                  </TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{depth === 0 ? 'Class' : 'Sub-class'}</TableCell>
                  <TableCell>
                    <StatusChip status={row.status} />
                  </TableCell>
                  {canChange && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        aria-label={`Edit ${row.code}`}
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setEditing(row);
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {creating && (
        <ClassDialog
          classes={rows}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {editing !== null && (
        <ClassDialog
          key={`${editing.id}:${String(editing.version)}`}
          assetClass={editing}
          classes={rows}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Stack>
  );
}

/** Create (no `assetClass`) or change one class. A change sends only what changed, with If-Match. */
function ClassDialog({
  assetClass,
  classes,
  onClose,
}: {
  assetClass?: AssetClassDto;
  classes: readonly AssetClassDto[];
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const creating = assetClass === undefined;
  const [code, setCode] = useState(assetClass?.code ?? '');
  const [name, setName] = useState(assetClass?.name ?? '');
  const [parent, setParent] = useState(assetClass?.parentClassId ?? '');
  const [status, setStatus] = useState<string>(assetClass?.status ?? 'active');
  const hasSubClasses = classes.some((c) => c.parentClassId === assetClass?.id);
  // Only an active top-level class can take sub-classes (class → sub-class).
  const parents = classes.filter(
    (c) =>
      c.parentClassId === null &&
      c.id !== assetClass?.id &&
      (c.status === 'active' || c.id === assetClass?.parentClassId),
  );

  const body: Record<string, string | null> = {};
  if (creating) {
    body.code = code.trim();
    body.name = name.trim();
    if (parent !== '') body.parentClassId = parent;
  } else {
    if (name.trim() !== assetClass.name) body.name = name.trim();
    if (parent !== (assetClass.parentClassId ?? ''))
      body.parentClassId = parent === '' ? null : parent;
    if (status !== assetClass.status) body.status = status;
  }
  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(CLASSES, body, AssetClassSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${CLASSES}/${assetClass.id}`,
            body,
            AssetClassSchema,
            session.data?.csrfToken ?? '',
            assetClass.version,
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assets', 'classes'] });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      title={creating ? 'New asset class' : `Edit asset class ${assetClass.code}`}
      submitLabel={creating ? 'Create class' : 'Save changes'}
      canSubmit={
        name.trim() !== '' && (creating ? code.trim() !== '' : Object.keys(body).length > 0)
      }
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Code"
        required
        autoFocus={creating}
        disabled={!creating}
        value={code}
        helperText={creating ? 'Upper case, e.g. IT-HW. It cannot change later.' : undefined}
        onChange={(e) => {
          setCode(e.target.value.toUpperCase());
        }}
      />
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <TextField
        select
        label="Parent class"
        value={parent}
        disabled={hasSubClasses}
        helperText={
          hasSubClasses
            ? 'This class has sub-classes, so it stays a top-level class.'
            : 'Choose a class to make this a sub-class of it.'
        }
        onChange={(e) => {
          setParent(e.target.value);
        }}
      >
        <MenuItem value="">None (top-level class)</MenuItem>
        {parents.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.code} — {c.name}
          </MenuItem>
        ))}
      </TextField>
      {!creating && (
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
          }}
          helperText="An inactive class cannot be given to new assets but stays on existing ones."
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      )}
      {!creating && status === 'inactive' && assetClass.status === 'active' && (
        <Alert severity="warning" variant="outlined">
          Deactivating {assetClass.code} stops it being chosen for new assets.
        </Alert>
      )}
    </FormDialog>
  );
}
